import { VehicleRegistrationFormat } from '@wrisk/ui-components'

import { ChangeConfig } from './changeConfig'
import { DisclosureConfig } from './disclosureConfig'
import { FormConfig, InputConfig } from './formBuilder'
import { ProductCondition } from './inputConditions'
import { ManagementConfig } from './management'

export interface ConfigurationState {
  config: StateConfig
}

export enum DocumentType {
  PRODUCT = 'product',
  POLICY = 'policy',
  STATIC = 'static',
}

export type DocumentFileType = 'HTML' | 'PDF'

interface Document {
  id: string
  type: DocumentType
  fileType?: DocumentFileType
}

export interface ProductDocument extends Document {
  type: DocumentType.PRODUCT
  coverCode?: string
}

export interface PolicyDocument extends Document {
  type: DocumentType.POLICY
}

export interface StaticDocument extends Document {
  type: DocumentType.STATIC
}

export type DocumentConfig = ProductDocument | PolicyDocument | StaticDocument

export type FormatsConfig = {
  phoneNumber: Record<'prefix' | 'regex', string>
  postalCode: Record<'regex', string>
  stripeToken: Record<'regex', string>
  vehicleRegistration: VehicleRegistrationFormat
}

export type Provider = string

interface StripeConfig {
  apiKey: string
  stripeAccount: string | undefined
}

export interface TwilioConfig {
  chatServer: string
}

export interface AmplitudeConfig {
  apiKey: string
}

export interface Localisation {
  locale: string
  currency: string
  timeZone: string
  fallbackLanguage: string
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface Config {
  localisation?: Partial<Localisation>
  sessionClient: any
  apiClient: any
  locationService: string
  app: any
  auth0: any
  state: any
  stripe: StripeConfig
  airbrake: any // Actually AirbrakeConfig
  logLevel: string
  twilio?: Partial<TwilioConfig>
  amplitude?: AmplitudeConfig
  environment: string
}

/* eslint-enable  @typescript-eslint/no-explicit-any */

export interface AppConfig {
  assetDomain: string
  cookieDomain: string
  auth0Domain: string
  provider: Provider
  title: string
  description: string
  themeColor: string
}

export interface Scheme {
  schemeCode: string
  productUrlName: string
  hidden?: boolean
  disabled?: boolean
}

export interface PrefillConfig {
  form?: FormConfig
}

export interface RetrieveConfig {
  backBooks?: string[]
}

export interface FeatureToggles {
  // Long-running toggles
  allowModifyBillingDay: boolean
  isAdmin: boolean
  multiQuote: boolean
  disableBind: boolean
  axitechFnol: boolean
  hideBilling?: boolean
}

export interface StateConfig {
  keys?: {
    maps?: string
  }
  formats: FormatsConfig
  productConfig: Record<string, ProductConfig>
  contentBaseUrl: string
  provider: Provider
  schemes: Scheme[]
  features: Partial<FeatureToggles>
  formElements: Record<string, FormConfig[0]>
  adjustments: Record<string, ChangeConfig>
  account?: InputConfig[]
  proposalTracking?: string[]
  createProposal?: {
    lookups?: Record<string, string>
    passthroughs?: string[]
  }
  quoteData?: string[]
  retrieve?: RetrieveConfig
  footer?: string[]
}

export enum ProductKind {
  SUBSCRIPTION = 'SUBSCRIPTION',
  ANNUAL = 'ANNUAL',
  DRIVEAWAY = 'DRIVEAWAY',
}

export interface ProposalConfirmationConfig {
  conditions: ProductCondition[]
  form: FormConfig
}

export interface FeedbackConfig {
  type: string
  actions: Record<string, string>
}

export type UploadsConfig = Record<
  string,
  Partial<{
    maxSize: number
    maxFiles: number
    accept: { [key: string]: string[] }
  }>
>

export interface VoluntaryExcessConfig {
  voluntaryExcessCode: string
  excesses: number[]
}

export type ExcessCombination = Record<string, number>

export interface ExcessConfig {
  isAdjustable?: boolean
  endorsements: string[]

  mainExcesses?: string[]
  otherExcesses?: string[]
  voluntaryExcesses?: VoluntaryExcessConfig[]
  coverLevels?: Record<string, ExcessCombination[]>
}

export interface CancellationCoolingOffConfig {
  type: 'CoolingOff'
  reasons?: string[]
  startAt: 'Inception' | 'Bind'
  days: number
}

export interface CancellationStandardConfig {
  type: 'Standard'
  reasons?: string[]
  daysToDefer?: number
}

export interface CancellationCustomerCareConfig {
  type: 'CustomerCare'
}

export type CancellationConfig =
  | CancellationCoolingOffConfig
  | CancellationStandardConfig
  | CancellationCustomerCareConfig

export interface RenewalConfig {
  allowModifyAutoRenewal?: boolean
  autoRenewalOptions?: Record<string, string>
  reasons: string[]
  sections: ManagementConfig[]
}

export type FinalMileConfig = Partial<{
  form: FormConfig
  validationProfile: string
}>

export type CheckType = 'verify' | 'id'

export interface ProductConfig {
  productKind: ProductKind
  assetTypes: AssetType[]
  endorsements?: string[]
  excess?: ExcessConfig
  cancellation?: CancellationConfig
  documents: DocumentConfig[]
  uploads?: UploadsConfig
  howItWorks?: HowItWorksSection[]
  paymentChecklist?: string[]
  disclosures: DisclosureConfig[]
  checks?: Record<CheckType, boolean>
  proposalConfirmation?: ProposalConfirmationConfig
  feedback?: Record<string, FeedbackConfig>
  management: ManagementConfig[]
  summary: ManagementConfig[]
  renewal: RenewalConfig
  accountSetup: FormConfig
  expiredProposal: FormConfig
  beforeWeBegin?: FormConfig
  finalMile?: FinalMileConfig
  payment?: PaymentConfig
  prefill?: PrefillConfig
}

export interface DynamicDataConfig {
  adjustments: string[]
  showSections: string[]
}

export interface OptionalCoverConfig {
  code: string
  form?: FormConfig
  contextualHelp?: DynamicDataConfig
}

export interface PaymentConfig {
  schedules: PaymentScheduleConfig[]
}

export type PaymentMethod = 'DIRECT_DEBIT' | 'CARD' | 'DUAL'
export type PaymentScheduleType = 'SINGLE_PAYMENT' | 'MONTHLY'

export interface PaymentScheduleConfig {
  type: PaymentScheduleType
  paymentMethods: PaymentMethod[]
}

export interface AssetType {
  code: string
  core?: boolean
  covers: {
    included: string[]
    conditional?: string[]
    optional?: OptionalCoverConfig[]
  }
}

export interface HowItWorksSection {
  id: string
  illusId: string
}
